<template>
    <AppModal :show-modal="openModal" @close="handleClose">
        <ManualBankingWarning class="mb-4" />

        <div>
            <div class="md:flex justify-between mb-3">
                <div class="flex-initial">
                    <label for="official_name">Name of Bank</label>
                </div>
                <div
                    v-if="getError('official_name')"
                    class="flex-initial mt-2 sm:mt-0 md:ml-2 font-semibold text-red-500"
                >
                    {{ getError("official_name") }}
                </div>
            </div>
            <div class="md:flex">
                <input
                    data-testid="official_name"
                    type="text"
                    tabindex="1"
                    id="official_name"
                    name="official_name"
                    placeholder="Name of Bank"
                    class="input-field w-full md:mr-1"
                    v-model="manualForm.official_name"
            />
            </div>
        </div>

        <div class="mt-4">
            <div class="md:flex justify-between mb-3">
                <div class="flex-initial">
                    <label for="account_number">Account Number</label>
                </div>
                <div
                    v-if="getError('account_number')"
                    class="flex-initial mt-2 sm:mt-0 md:ml-2 font-semibold text-red-500"
                >
                    {{ getError("account_number") }}
                </div>
            </div>
            <div class="md:flex">
                <input
                    data-testid="account_number"
                    type="text"
                    tabindex="1"
                    id="account_number"
                    name="account_number"
                    placeholder="Account Number"
                    class="input-field w-full md:mr-1"
                    v-model="manualForm.account_number"
                />
                <input
                    data-testid="account_number_confirmation"
                    type="text"
                    tabindex="1"
                    id="account_number"
                    name="account_number_confirmation"
                    placeholder="Confirmation"
                    class="input-field w-full mt-1 md:mt-0 md:ml-1"
                    v-model="manualForm.account_number_confirmation"
                />
            </div>
        </div>

        <div class="mt-4">
            <div class="md:flex justify-between mb-3">
                <div class="flex-initial">
                    <label for="routing_number">Routing Number</label>
                </div>
                <div
                    v-if="getError('routing_number')"
                    class="flex-initial mt-2 sm:mt-0 md:ml-2 font-semibold text-red-500"
                >
                    {{ getError("routing_number") }}
                </div>
            </div>
            <div class="md:flex">
                <input
                    data-testid="routing_number"
                    type="text"
                    tabindex="1"
                    id="routing_number"
                    name="routing_number"
                    placeholder="Routing Number"
                    class="input-field w-full md:mr-1"
                    v-model="manualForm.routing_number"
                />
                <input
                    data-testid="routing_number_confirmation"
                    type="text"
                    tabindex="1"
                    id="routing_number"
                    name="routing_number_confirmation"
                    placeholder="Confirmation"
                    class="input-field w-full mt-1 md:mt-0 md:ml-1"
                    v-model="manualForm.routing_number_confirmation"
                />
            </div>
        </div>

        <div class="mt-4">
            <div class="md:flex justify-between mb-3">
                <div class="flex-initial">
                    <label for="bank_statement">Bank Statement</label>
                </div>
                <div
                    v-if="getError('bank_statement')"
                    class="flex-initial mt-2 sm:mt-0 md:ml-2 font-semibold text-red-500" 
                    data-testid="bank_statement_error"
                >
                    {{ getError("bank_statement") }}
                </div>
            </div>
            <div class="md:flex">
                <input
                    data-testid="bank_statement"
                    ref="fileUpload"
                    type="file"
                    name="bank_statement"
                    id="bank_statement"
                    @change="$refs['fileUpload'].files.length ? (fileName = $refs['fileUpload'].files[0].name) : null"
                    class="hidden"
                />
                <div
                    @click.prevent="$refs['fileUpload'].click()"
                    class="bg-white p-14 w-full flex justify-center items-center font-semibold text-lg border-2 border-dashed border-gray-200 rounded-lg cursor-pointer"
                >
                    <template v-if="!fileName">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="w-8 h-8 mr-2"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                            />
                        </svg>
                        Upload Document <small class="text-red-500 inline-block ml-1"> (PDF Only)</small>
                    </template>
                    <template v-else>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="w-8 h-8 mr-2"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M10.125 2.25h-4.5c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125v-9M10.125 2.25h.375a9 9 0 019 9v.375M10.125 2.25A3.375 3.375 0 0113.5 5.625v1.5c0 .621.504 1.125 1.125 1.125h1.5a3.375 3.375 0 013.375 3.375M9 15l2.25 2.25L15 12"
                            />
                        </svg>
                        {{ fileName }}
                    </template>
                </div>
            </div>
        </div>

        <div class="flex justify-center items-center mt-8">
            <AppButton 
                data-testid="submit" 
                :loading="loading" 
                :style="'solid'"
                @click="submitManual">Submit</AppButton>
        </div>
        
        <AppErrorPopup 
            :show="showErrorPopup" 
            :message="errorMessage" 
            @close="closeErrorPopup" 
        />
    </AppModal>
</template>

<script>
import AppButton from "./AppButton";
import AppModal from "./AppModal";
import AppErrorPopup from "./AppErrorPopup";
import ManualBankingWarning from "./ManualBankingWarning.vue";
import { capitalize } from "../utils";
import { analytics } from "./../analytics";

export default {
    components: {
        AppModal,
        AppButton,
        AppErrorPopup,
        ManualBankingWarning
    },

    props: {
        close: {
            type: Function,
            default: () => {},
        },
        loan: {
            type: Object,
            default: {},
        },
        proceed: {
            type: Function,
            default: () => {},
        },
        showModal: {
            type: Boolean,
            default: false,
        },
        isChangingBankFlow: {
            type: Boolean,
            default: false,
        },
        gPageName: {
            type: String,
            default: "",
        },
        gEventType: {
            type: String,
            default: "",
        },
    },

    data() {
        return {
            fileName: "",
            loading: false,
            openModal: false,
            manualForm: {
                official_name: "",
                account_number: "",
                account_number_confirmation: "",
                routing_number: "",
                routing_number_confirmation: "",
                bank_statement: "",
                errors: {},
            },
            // error popup state
            showErrorPopup: false,
            errorMessage: ''
        };
    },

    watch: {
        showModal(val) {
            this.openModal = val;
        },
    },


    mounted() {
        if (this.manual) {
            this.showManual = true;
        }

        if (this.loan.banking_account) {
            this.manualForm.official_name = capitalize(this.loan.banking_account?.official_name ?? '');
        }
    },

    methods: {
        submitManual() {
            if (!this.fileName) {
                this.manualForm.errors = { bank_statement: ['Please upload your bank statement'] };
                return;
            }

            this.loading = true;

            let formData = new FormData();

            for (const [key, value] of Object.entries(this.manualForm)) {
                formData.append(key, value);
            }

            if (this.fileName) {
                formData.append("bank_statement", this.$refs["fileUpload"].files[0]);
            }

            formData.append("is_changing_bank_flow", this.isChangingBankFlow);
            formData.append("onboarding_attempt", true);

            analytics.dinamicPageEvents(this.gEventType, this.gPageName).handleSubmitManualAccountModal({
                loan_id: this.loan?.id ?? null,
                user_id: this.loan?.user_id ?? this.user?.id ?? null,
                loan_status: this.loan?.status ?? null,
            });

            this.$http
                .post(`/signup/verify-bank-account/${this.loan.id}/enter-details`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                })
                .then(
                    () => {
                        this.loading = false;
                        this.openModal = false;
                        window.location.reload();
                    },
                    (error) => {                        
                        this.loading = false;
                        
                        if (error.response && error.response.status === 422 && error.response.data?.onboarding_way) {
                            this.showErrorPopup = true;
                            this.errorMessage = error.response.data.onboarding_way;
                        } else if (error.response?.status === 413) {
                            this.manualForm.errors = {
                                bank_statement: ["Please upload a smaller file, up to 20MB"]
                            };
                        } else {
                            this.manualForm.errors = error.response.data.errors;
                        }

                        analytics.dinamicPageEvents(this.gEventType, this.gPageName).handleSubmitManualAccountModalError({
                            loan_id: this.loan?.id ?? null,
                            user_id: this.loan?.user_id ?? this.user?.id ?? null,
                            page_name: this.gPageName,
                            loan_status: this.loan?.status ?? null,
                            error_fields: Object.keys(this.manualForm.errors ?? {}),
                        });
                    }
                );
        },

        getError(errorName) {
            return (
                this.manualForm.errors &&
                this.manualForm.errors[errorName] &&
                this.manualForm.errors[errorName].join(", ")
            );
        },

        handleClose() {
            this.$emit("close");
            this.openModal = false;
        },

        clearErrors() {
            this.errors = {};
        },

        clearError(field) {
            if (this.errors && this.errors[field]) {
                this.errors[field] = [];
            }
        },

        handleProceed() {
            this.openModal = false;
            this.$emit("proceed");
        },

        /**
         * Close the error popup
         */
        closeErrorPopup() {
            this.showErrorPopup = false;
            this.errorMessage = '';
            window.location.reload();
        },
    },
};
</script>

<style scoped>
.modal {
    position: absolute;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: auto;
    text-align: center;
    height: fit-content;
    padding: 2rem;
    border-radius: 1rem;
    box-shadow: 0 5px 5px rgba(0, 0, 0, 0.2);
    background: #fff;
    z-index: 999;
    transform: none;
}
.modal h1 {
    margin: 0 0 1rem;
}

.modal-overlay {
    content: "";
    position: absolute;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 998;
    background: #2c3e50;
    opacity: 0.6;
    cursor: pointer;
}

/* ---------------------------------- */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.4s linear;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.pop-enter-active,
.pop-leave-active {
    transition: transform 0.4s cubic-bezier(0.5, 0, 0.5, 1), opacity 0.4s linear;
}

.pop-enter,
.pop-leave-to {
    opacity: 0;
    transform: scale(0.3) translateY(-50%);
}
</style>
